import apiClient from '../../../../shared/lib/api'

export type CompanyVideoStatus = 'uploading' | 'ready' | 'deleted'
export type CleanStatus = 'pending' | 'processing' | 'ready' | 'failed'
export type AIAnalysisStatus = 'pending' | 'processing' | 'ready' | 'failed'
export type SceneAnalysisStatus = 'pending' | 'processing' | 'ready' | 'failed'

export interface CompanyVideoAsset {
  id: string
  tenantId: string
  title: string
  description?: string
  category?: string
  brandId?: string
  tags?: string[]
  status: CompanyVideoStatus
  fileName: string
  contentType?: string
  sizeBytes?: number
  s3Key: string
  cleanVersionS3Key?: string
  cleanStatus?: CleanStatus
  createdAt: string
  updatedAt: string
  deletedAt?: string
  thumbnailS3Key?: string
  // AI analysis fields
  aiAnalysisStatus?: AIAnalysisStatus
  aiDescription?: string
  contentRole?: string
  actionType?: string
  voice?: string
  shotSize?: string
  pointOfView?: string
  quality?: string
  mood?: string
  semanticKeywords?: string[]
  transcript?: string
  analyzedAt?: string
  durationSec?: number
  // Scene analysis fields
  sceneAnalysisStatus?: SceneAnalysisStatus
  sceneAnalysis?: string // JSON array of scenes
  sceneCount?: number
  sceneAnalysisError?: string
  // Import tracking
  sourceAdId?: string
}

export type CompanyVideoWithThumbnail = CompanyVideoAsset & {
  thumbnailUrl?: string
}

export interface ListCompanyVideosResponse {
  videos: CompanyVideoWithThumbnail[]
  nextCursor?: string
}

export interface CreateCompanyVideoResponse {
  video: CompanyVideoAsset
  uploadUrl: string
  uploadHeaders?: Record<string, string>
  expiresIn?: number
}

export interface GetCompanyVideoResponse {
  video: CompanyVideoAsset
  downloadUrl?: string
  cleanVersionUrl?: string
  thumbnailUrl?: string
}

export interface CreateCompanyVideoInput {
  title?: string
  description?: string
  category?: string
  tags?: string[]
  fileName: string
  contentType?: string
  sizeBytes?: number
}

export interface UpdateCompanyVideoInput {
  title?: string
  description?: string
  category?: string
  tags?: string[]
}

export interface CompleteCompanyVideoInput {
  sizeBytes?: number
  contentType?: string
  createSceneClips?: boolean // If true, analyze video for scenes and create clips
}

export interface ListCompanyVideosParams {
  limit?: number
  cursor?: string
  q?: string
  tag?: string
  category?: string
  contentRole?: string
  voice?: string
  shotSize?: string
  pointOfView?: string
  quality?: string
  actionType?: string
  aiStatus?: string
}

export async function listCompanyVideos(params: ListCompanyVideosParams): Promise<ListCompanyVideosResponse> {
  const res = await apiClient.get<ListCompanyVideosResponse>('/company-videos', { params })
  return res.data
}

export async function createCompanyVideo(input: CreateCompanyVideoInput): Promise<CreateCompanyVideoResponse> {
  const res = await apiClient.post<CreateCompanyVideoResponse>('/company-videos', input)
  return res.data
}

export async function completeCompanyVideoUpload(videoId: string, input: CompleteCompanyVideoInput) {
  const res = await apiClient.post<{ video: CompanyVideoAsset }>(`/company-videos/${videoId}/complete`, input)
  return res.data.video
}

export async function getCompanyVideo(videoId: string): Promise<GetCompanyVideoResponse> {
  const res = await apiClient.get<GetCompanyVideoResponse>(`/company-videos/${videoId}`)
  return res.data
}

export async function updateCompanyVideo(videoId: string, input: UpdateCompanyVideoInput) {
  const res = await apiClient.put<{ video: CompanyVideoAsset }>(`/company-videos/${videoId}`, input)
  return res.data.video
}

export async function deleteCompanyVideo(videoId: string) {
  await apiClient.delete(`/company-videos/${videoId}`)
}

export type UploadProgressPhase = 'creating' | 'uploading' | 'completing' | 'completed'

export interface UploadProgressCallback {
  (phase: UploadProgressPhase, videoId?: string): void
}

export async function uploadCompanyVideo(params: {
  file: File
  title?: string
  description?: string
  category?: string
  tags?: string[]
  createSceneClips?: boolean // If true, analyze video for scenes and create clips
  onProgress?: UploadProgressCallback
}) {
  const { file, onProgress, createSceneClips } = params

  // Phase 1: Create video entry
  onProgress?.('creating')
  const createRes = await createCompanyVideo({
    fileName: file.name,
    contentType: file.type || undefined,
    sizeBytes: file.size || undefined,
    title: params.title,
    description: params.description,
    category: params.category,
    tags: params.tags,
  })

  if (!createRes?.video?.id || !createRes.uploadUrl) {
    throw new Error('Create response missing uploadUrl/video')
  }

  const videoId = createRes.video.id

  // Phase 2: Upload to S3
  onProgress?.('uploading', videoId)
  const putRes = await fetch(createRes.uploadUrl, {
    method: 'PUT',
    headers: createRes.uploadHeaders || {},
    body: file,
  })

  if (!putRes.ok) {
    const text = await putRes.text().catch(() => '')
    throw new Error(`S3 upload failed (${putRes.status}): ${text || putRes.statusText}`)
  }

  // Phase 3: Complete upload (queues AI analysis + clean + thumbnail via SQS)
  // If createSceneClips is enabled, the backend will use scene-based analysis
  // which detects individual scenes and creates clips for each one
  onProgress?.('completing', videoId)
  const video = await completeCompanyVideoUpload(createRes.video.id, {
    sizeBytes: file.size || undefined,
    contentType: file.type || undefined,
    createSceneClips,
  })

  // Phase 4: Done — AI analysis runs async in the background
  onProgress?.('completed', videoId)
  return video
}

export async function reanalyzeCompanyVideo(videoId: string, options?: { brandContext?: string }): Promise<CompanyVideoAsset> {
  await apiClient.post(`/company-videos/${videoId}/reanalyze`, {
    ...(options?.brandContext ? { brandContext: options.brandContext } : {}),
  })
  // Re-analysis is now async (queued via SQS). Fetch current state.
  const fresh = await getCompanyVideo(videoId)
  return fresh.video
}

export async function recleanCompanyVideo(videoId: string): Promise<void> {
  await apiClient.post(`/company-videos/${videoId}/re-clean`)
}

// Video scene type (from scene analysis)
export interface VideoScene {
  sceneNumber: number
  startTime: number
  endTime: number
  description: string
  contentRole: string
  visualType?: string
  audio?: string
  textOverlay?: string
  mood?: string
  keyElements?: string[]
  voice?: string
  pointOfView?: string
  shotSize?: string
  quality?: string
  semanticKeywords?: string[]
  actionType?: string
}

// Helper to parse scene analysis JSON
export function parseSceneAnalysis(video: CompanyVideoAsset): VideoScene[] {
  if (!video.sceneAnalysis) return []
  try {
    return JSON.parse(video.sceneAnalysis) as VideoScene[]
  } catch {
    return []
  }
}

// Scene-based search types
export interface SceneSearchParams {
  description: string
  visualType?: string
  mood?: string
  camera?: string
  audio?: string
  keyElements?: string[]
  durationSec?: number
}

export interface SceneSuggestion {
  video: CompanyVideoAsset
  score: number
  matchReasons: string[]
}

export interface SceneSearchResponse {
  suggestions: SceneSuggestion[]
}

export async function searchVideosByScene(
  scene: SceneSearchParams,
  limit?: number
): Promise<SceneSearchResponse> {
  const res = await apiClient.post<SceneSearchResponse>('/company-videos/search-by-scene', {
    scene,
    limit: limit || 10,
  })
  return res.data
}

